export type ConnectionTemplate = {
  id: string;
  name: string;
  description: string;
  category:
    | "microsoft"
    | "google"
    | "aws"
    | "api"
    | "database"
    | "document"
    | "generic";
  icon: string;
  type: "rest_api" | "graphql" | "database" | "webhook" | "custom";
  auth_type: string;
  base_url: string;
  required_fields: string[];
  optional_fields?: string[];
  auth_config_template?: Record<string, unknown>;
  config_template?: Record<string, unknown>;
  openapi_url?: string;
  setup_guide_url?: string;
};

export const CATEGORIES = [
  { id: "all", name: "All", dot: "bg-white/40" },
  { id: "microsoft", name: "Microsoft", dot: "bg-blue-400" },
  { id: "google", name: "Google", dot: "bg-sky-400" },
  { id: "aws", name: "AWS", dot: "bg-amber-400" },
  { id: "api", name: "REST APIs", dot: "bg-teal-400" },
  { id: "database", name: "Databases", dot: "bg-violet-400" },
  { id: "document", name: "Documents", dot: "bg-rose-400" },
  { id: "generic", name: "Generic", dot: "bg-white/25" },
] as const;

export const TEMPLATES: ConnectionTemplate[] = [
  {
    id: "microsoft_graph",
    name: "Microsoft Graph",
    description: "Access Microsoft 365 data - users, mail, calendar, files",
    category: "microsoft",
    icon: "🔷",
    type: "rest_api",
    auth_type: "oauth2_client",
    base_url: "https://graph.microsoft.com/v1.0",
    required_fields: ["tenant_id", "client_id", "client_secret"],
    optional_fields: ["scope"],
    auth_config_template: {
      token_url: "https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token",
      scope: "https://graph.microsoft.com/.default",
      grant_type: "client_credentials",
    },
    openapi_url: "https://raw.githubusercontent.com/microsoftgraph/msgraph-metadata/master/openapi/v1.0/openapi.yaml",
    setup_guide_url: "https://learn.microsoft.com/en-us/graph/auth-v2-service",
  },
  {
    id: "azure_management",
    name: "Azure Management",
    description: "Manage Azure resources - VMs, storage, networking",
    category: "microsoft",
    icon: "☁️",
    type: "rest_api",
    auth_type: "oauth2_client",
    base_url: "https://management.azure.com",
    required_fields: ["tenant_id", "client_id", "client_secret", "subscription_id"],
    auth_config_template: {
      token_url: "https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token",
      scope: "https://management.azure.com/.default",
      grant_type: "client_credentials",
    },
    setup_guide_url: "https://learn.microsoft.com/en-us/azure/azure-resource-manager/management/overview",
  },
  {
    id: "dynamics_365",
    name: "Dynamics 365",
    description: "CRM and ERP data - accounts, contacts, opportunities",
    category: "microsoft",
    icon: "📊",
    type: "rest_api",
    auth_type: "oauth2_client",
    base_url: "https://{org_name}.crm.dynamics.com/api/data/v9.2",
    required_fields: ["tenant_id", "client_id", "client_secret", "org_name"],
    auth_config_template: {
      token_url: "https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token",
      scope: "https://{org_name}.crm.dynamics.com/.default",
      grant_type: "client_credentials",
    },
    setup_guide_url: "https://learn.microsoft.com/en-us/dynamics365/customerengagement/on-premises/developer/webapi/authenticate-web-api",
  },
  {
    id: "sharepoint_online",
    name: "SharePoint Online",
    description: "Document libraries, lists, and site content",
    category: "microsoft",
    icon: "📁",
    type: "rest_api",
    auth_type: "oauth2_client",
    base_url: "https://{tenant}.sharepoint.com/_api",
    required_fields: ["tenant_id", "client_id", "client_secret", "sharepoint_tenant"],
    auth_config_template: {
      token_url: "https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token",
      scope: "https://{tenant}.sharepoint.com/.default",
      grant_type: "client_credentials",
    },
    setup_guide_url: "https://learn.microsoft.com/en-us/sharepoint/dev/sp-add-ins/get-to-know-the-sharepoint-rest-service",
  },
  {
    id: "power_bi",
    name: "Power BI",
    description: "Dashboards, reports, and datasets",
    category: "microsoft",
    icon: "📈",
    type: "rest_api",
    auth_type: "oauth2_client",
    base_url: "https://api.powerbi.com/v1.0/myorg",
    required_fields: ["tenant_id", "client_id", "client_secret"],
    auth_config_template: {
      token_url: "https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token",
      scope: "https://analysis.windows.net/powerbi/api/.default",
      grant_type: "client_credentials",
    },
    setup_guide_url: "https://learn.microsoft.com/en-us/power-bi/developer/embedded/embed-service-principal",
  },
  {
    id: "microsoft_intune",
    name: "Microsoft Intune",
    description: "Device management - managed devices, compliance, configuration",
    category: "microsoft",
    icon: "🔒",
    type: "rest_api",
    auth_type: "oauth2_client",
    base_url: "https://graph.microsoft.com/v1.0",
    required_fields: ["tenant_id", "client_id", "client_secret"],
    optional_fields: ["scope"],
    auth_config_template: {
      token_url: "https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token",
      scope: "https://graph.microsoft.com/.default",
      grant_type: "client_credentials",
    },
    setup_guide_url: "https://learn.microsoft.com/en-us/mem/intune/developer/intune-graph-apis",
  },
  {
    id: "google_workspace",
    name: "Google Workspace",
    description: "Access Gmail, Drive, Calendar, Sheets via service account",
    category: "google",
    icon: "🔵",
    type: "rest_api",
    auth_type: "jwt",
    base_url: "https://www.googleapis.com",
    required_fields: ["service_account_email", "private_key", "scopes"],
    optional_fields: ["delegated_user_email"],
    auth_config_template: {
      grant_type: "urn:ietf:params:oauth:grant-type:jwt-bearer",
      assertion_type: "service_account",
    },
    setup_guide_url: "https://developers.google.com/identity/protocols/oauth2/service-account",
  },
  {
    id: "gmail_api",
    name: "Gmail API",
    description: "Read, send, and manage email messages",
    category: "google",
    icon: "📧",
    type: "rest_api",
    auth_type: "jwt",
    base_url: "https://gmail.googleapis.com/gmail/v1",
    required_fields: ["service_account_email", "private_key"],
    optional_fields: ["delegated_user_email"],
    config_template: { scopes: ["https://www.googleapis.com/auth/gmail.readonly"] },
    openapi_url: "https://gmail.googleapis.com/$discovery/rest?version=v1",
    setup_guide_url: "https://developers.google.com/gmail/api/quickstart/python",
  },
  {
    id: "google_drive",
    name: "Google Drive",
    description: "Files, folders, and shared drives",
    category: "google",
    icon: "💾",
    type: "rest_api",
    auth_type: "jwt",
    base_url: "https://www.googleapis.com/drive/v3",
    required_fields: ["service_account_email", "private_key"],
    optional_fields: ["delegated_user_email"],
    config_template: { scopes: ["https://www.googleapis.com/auth/drive"] },
    openapi_url: "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
    setup_guide_url: "https://developers.google.com/drive/api/quickstart/python",
  },
  {
    id: "google_calendar",
    name: "Google Calendar",
    description: "Events, calendars, and scheduling",
    category: "google",
    icon: "📅",
    type: "rest_api",
    auth_type: "jwt",
    base_url: "https://www.googleapis.com/calendar/v3",
    required_fields: ["service_account_email", "private_key"],
    optional_fields: ["delegated_user_email"],
    config_template: { scopes: ["https://www.googleapis.com/auth/calendar"] },
    openapi_url: "https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest",
    setup_guide_url: "https://developers.google.com/calendar/api/quickstart/python",
  },
  {
    id: "google_sheets",
    name: "Google Sheets",
    description: "Spreadsheets and data manipulation",
    category: "google",
    icon: "📊",
    type: "rest_api",
    auth_type: "jwt",
    base_url: "https://sheets.googleapis.com/v4",
    required_fields: ["service_account_email", "private_key"],
    optional_fields: ["delegated_user_email"],
    config_template: { scopes: ["https://www.googleapis.com/auth/spreadsheets"] },
    openapi_url: "https://sheets.googleapis.com/$discovery/rest?version=v4",
    setup_guide_url: "https://developers.google.com/sheets/api/quickstart/python",
  },
  {
    id: "google_cloud",
    name: "Google Cloud APIs",
    description: "Cloud Storage, BigQuery, Compute Engine",
    category: "google",
    icon: "☁️",
    type: "rest_api",
    auth_type: "jwt",
    base_url: "https://storage.googleapis.com",
    required_fields: ["service_account_email", "private_key", "project_id"],
    config_template: { scopes: ["https://www.googleapis.com/auth/cloud-platform"] },
    setup_guide_url: "https://cloud.google.com/docs/authentication/getting-started",
  },
  {
    id: "aws_generic",
    name: "AWS API",
    description: "Generic AWS service connection with Signature V4",
    category: "aws",
    icon: "🟠",
    type: "rest_api",
    auth_type: "aws_signature",
    base_url: "https://{service}.{region}.amazonaws.com",
    required_fields: ["access_key_id", "secret_access_key", "region", "service"],
    optional_fields: ["session_token"],
    auth_config_template: { algorithm: "AWS4-HMAC-SHA256", signature_version: "v4" },
    setup_guide_url: "https://docs.aws.amazon.com/IAM/latest/UserGuide/reference_sigv.html",
  },
  {
    id: "aws_s3",
    name: "AWS S3",
    description: "Object storage - buckets and files",
    category: "aws",
    icon: "🪣",
    type: "rest_api",
    auth_type: "aws_signature",
    base_url: "https://s3.{region}.amazonaws.com",
    required_fields: ["access_key_id", "secret_access_key", "region"],
    optional_fields: ["session_token"],
    config_template: { service: "s3" },
    setup_guide_url: "https://docs.aws.amazon.com/AmazonS3/latest/API/Welcome.html",
  },
  {
    id: "aws_dynamodb",
    name: "AWS DynamoDB",
    description: "NoSQL database tables and items",
    category: "aws",
    icon: "🗃️",
    type: "rest_api",
    auth_type: "aws_signature",
    base_url: "https://dynamodb.{region}.amazonaws.com",
    required_fields: ["access_key_id", "secret_access_key", "region"],
    optional_fields: ["session_token"],
    config_template: { service: "dynamodb" },
    setup_guide_url: "https://docs.aws.amazon.com/amazondynamodb/latest/APIReference/Welcome.html",
  },
  {
    id: "aws_lambda",
    name: "AWS Lambda",
    description: "Serverless functions management",
    category: "aws",
    icon: "⚡",
    type: "rest_api",
    auth_type: "aws_signature",
    base_url: "https://lambda.{region}.amazonaws.com",
    required_fields: ["access_key_id", "secret_access_key", "region"],
    optional_fields: ["session_token"],
    config_template: { service: "lambda" },
    setup_guide_url: "https://docs.aws.amazon.com/lambda/latest/api/Welcome.html",
  },
  {
    id: "aws_api_gateway",
    name: "AWS API Gateway",
    description: "Custom API endpoints on AWS",
    category: "aws",
    icon: "🚪",
    type: "rest_api",
    auth_type: "aws_signature",
    base_url: "https://{api_id}.execute-api.{region}.amazonaws.com/{stage}",
    required_fields: ["access_key_id", "secret_access_key", "region", "api_id", "stage"],
    optional_fields: ["session_token"],
    config_template: { service: "execute-api" },
    setup_guide_url: "https://docs.aws.amazon.com/apigateway/latest/api/Welcome.html",
  },
  {
    id: "aws_secrets_manager",
    name: "AWS Secrets Manager",
    description: "Secure secrets storage and retrieval",
    category: "aws",
    icon: "🔐",
    type: "rest_api",
    auth_type: "aws_signature",
    base_url: "https://secretsmanager.{region}.amazonaws.com",
    required_fields: ["access_key_id", "secret_access_key", "region"],
    optional_fields: ["session_token"],
    config_template: { service: "secretsmanager" },
    setup_guide_url: "https://docs.aws.amazon.com/secretsmanager/latest/apireference/Welcome.html",
  },
  {
    id: "github",
    name: "GitHub",
    description: "Repositories, issues, pull requests, and more",
    category: "api",
    icon: "🐙",
    type: "rest_api",
    auth_type: "bearer_token",
    base_url: "https://api.github.com",
    required_fields: ["token"],
    openapi_url: "https://raw.githubusercontent.com/github/rest-api-description/main/descriptions/api.github.com/api.github.com.yaml",
    setup_guide_url: "https://docs.github.com/en/rest/authentication",
  },
  {
    id: "slack",
    name: "Slack",
    description: "Messages, channels, and workspace data",
    category: "api",
    icon: "💬",
    type: "rest_api",
    auth_type: "bearer_token",
    base_url: "https://slack.com/api",
    required_fields: ["token"],
    setup_guide_url: "https://api.slack.com/authentication",
  },
  {
    id: "stripe",
    name: "Stripe",
    description: "Payments, customers, subscriptions",
    category: "api",
    icon: "💳",
    type: "rest_api",
    auth_type: "bearer_token",
    base_url: "https://api.stripe.com/v1",
    required_fields: ["api_key"],
    openapi_url: "https://raw.githubusercontent.com/stripe/openapi/master/openapi/spec3.yaml",
    setup_guide_url: "https://stripe.com/docs/api/authentication",
  },
  {
    id: "twilio",
    name: "Twilio",
    description: "SMS, voice, and communication APIs",
    category: "api",
    icon: "📱",
    type: "rest_api",
    auth_type: "basic_auth",
    base_url: "https://api.twilio.com/2010-04-01",
    required_fields: ["account_sid", "auth_token"],
    setup_guide_url: "https://www.twilio.com/docs/usage/api",
  },
  {
    id: "sendgrid",
    name: "SendGrid",
    description: "Email delivery and marketing",
    category: "api",
    icon: "📤",
    type: "rest_api",
    auth_type: "bearer_token",
    base_url: "https://api.sendgrid.com/v3",
    required_fields: ["api_key"],
    setup_guide_url: "https://docs.sendgrid.com/api-reference/how-to-use-the-sendgrid-v3-api",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    description: "CRM, marketing, and sales data",
    category: "api",
    icon: "🧡",
    type: "rest_api",
    auth_type: "bearer_token",
    base_url: "https://api.hubapi.com",
    required_fields: ["access_token"],
    setup_guide_url: "https://developers.hubspot.com/docs/api/intro-to-auth",
  },
  {
    id: "salesforce",
    name: "Salesforce",
    description: "CRM - accounts, contacts, leads, opportunities",
    category: "api",
    icon: "☁️",
    type: "rest_api",
    auth_type: "oauth2_client",
    base_url: "https://{instance}.my.salesforce.com/services/data/v{api_version}",
    required_fields: ["instance", "client_id", "client_secret", "api_version"],
    auth_config_template: {
      token_url: "https://login.salesforce.com/services/oauth2/token",
      grant_type: "client_credentials",
    },
    setup_guide_url: "https://developer.salesforce.com/blogs/2024/02/invoke-rest-apis-with-the-salesforce-integration-user-and-oauth-client-credentials",
  },
  {
    id: "acumatica",
    name: "Acumatica ERP",
    description: "Cloud ERP - sales orders, inventory, financials, CRM",
    category: "api",
    icon: "📦",
    type: "rest_api",
    auth_type: "bearer_token",
    base_url: "https://{instance}/entity/Default/{api_version}",
    required_fields: ["instance", "api_version", "access_token"],
    auth_config_template: { token_type: "Bearer" },
    setup_guide_url: "https://help.acumatica.com/(W(8))/Wiki/ShowWiki.aspx?pageid=ed9fe5ab-b3c0-444c-8335-ff3b0c8392d6",
  },
  {
    id: "jsonplaceholder",
    name: "JSONPlaceholder",
    description: "Free fake API for testing and prototyping",
    category: "api",
    icon: "🧪",
    type: "rest_api",
    auth_type: "none",
    base_url: "https://jsonplaceholder.typicode.com",
    required_fields: [],
    setup_guide_url: "https://jsonplaceholder.typicode.com/guide/",
  },
  {
    id: "forgejo_codeberg",
    name: "Forgejo (Codeberg)",
    description: "Open-source git hosting — repos, issues, users via Codeberg's public demo",
    category: "api",
    icon: "🦊",
    type: "rest_api",
    auth_type: "none",
    base_url: "https://codeberg.org",
    required_fields: [],
    optional_fields: ["token"],
    openapi_url: "https://codeberg.org/swagger.v1.json",
    setup_guide_url: "https://forgejo.org/docs/latest/user/api-usage/",
  },
  {
    id: "discourse_meta",
    name: "Discourse (Meta)",
    description: "Open-source forum — topics, posts, categories via meta.discourse.org",
    category: "api",
    icon: "💬",
    type: "rest_api",
    auth_type: "none",
    base_url: "https://meta.discourse.org",
    required_fields: [],
    optional_fields: ["api_key", "api_username"],
    openapi_url: "https://docs.discourse.org/openapi.json",
    setup_guide_url: "https://docs.discourse.org/",
  },
  {
    id: "pokeapi",
    name: "PokéAPI",
    description: "Public OSS reference API — fully read-only, no auth",
    category: "api",
    icon: "🔴",
    type: "rest_api",
    auth_type: "none",
    base_url: "https://pokeapi.co/api/v2",
    required_fields: [],
    openapi_url: "https://raw.githubusercontent.com/PokeAPI/pokeapi/master/openapi.yml",
    setup_guide_url: "https://pokeapi.co/docs/v2",
  },
  {
    id: "wikipedia_rest",
    name: "Wikipedia REST",
    description: "Wikimedia REST v1 — page summaries, content, search (no auth)",
    category: "api",
    icon: "📖",
    type: "rest_api",
    auth_type: "none",
    base_url: "https://en.wikipedia.org/api/rest_v1",
    required_fields: [],
    openapi_url: "https://en.wikipedia.org/api/rest_v1/?spec",
    setup_guide_url: "https://en.wikipedia.org/api/rest_v1/",
  },
  {
    id: "postgresql",
    name: "PostgreSQL",
    description: "Relational database with advanced features",
    category: "database",
    icon: "🐘",
    type: "database",
    auth_type: "basic_auth",
    base_url: "",
    required_fields: ["host", "port", "database", "username", "password"],
    optional_fields: ["ssl_mode"],
    config_template: { port: 5432, ssl_mode: "prefer" },
    setup_guide_url: "https://www.postgresql.org/docs/current/libpq-connect.html",
  },
  {
    id: "mysql",
    name: "MySQL",
    description: "Popular open-source relational database",
    category: "database",
    icon: "🐬",
    type: "database",
    auth_type: "basic_auth",
    base_url: "",
    required_fields: ["host", "port", "database", "username", "password"],
    config_template: { port: 3306 },
    setup_guide_url: "https://dev.mysql.com/doc/refman/8.0/en/connecting.html",
  },
  {
    id: "mongodb",
    name: "MongoDB",
    description: "Document-oriented NoSQL database",
    category: "database",
    icon: "🍃",
    type: "database",
    auth_type: "basic_auth",
    base_url: "",
    required_fields: ["connection_string"],
    optional_fields: ["database"],
    setup_guide_url: "https://www.mongodb.com/docs/manual/reference/connection-string/",
  },
  {
    id: "redis",
    name: "Redis",
    description: "In-memory data structure store",
    category: "database",
    icon: "🔴",
    type: "database",
    auth_type: "api_key",
    base_url: "",
    required_fields: ["host", "port"],
    optional_fields: ["password", "database"],
    config_template: { port: 6379 },
    setup_guide_url: "https://redis.io/docs/connect/",
  },
  {
    id: "supabase",
    name: "Supabase",
    description: "Open source Firebase alternative with PostgreSQL",
    category: "database",
    icon: "⚡",
    type: "rest_api",
    auth_type: "api_key",
    base_url: "https://{project_ref}.supabase.co/rest/v1",
    required_fields: ["project_ref", "api_key"],
    auth_config_template: { api_key_name: "apikey", api_key_location: "header" },
    setup_guide_url: "https://supabase.com/docs/guides/api",
  },
  {
    id: "notion",
    name: "Notion",
    description: "Databases, pages, and workspace content",
    category: "document",
    icon: "📝",
    type: "rest_api",
    auth_type: "bearer_token",
    base_url: "https://api.notion.com/v1",
    required_fields: ["integration_token"],
    config_template: { headers: { "Notion-Version": "2022-06-28" } },
    setup_guide_url: "https://developers.notion.com/docs/getting-started",
  },
  {
    id: "confluence",
    name: "Confluence",
    description: "Wiki pages, spaces, and content",
    category: "document",
    icon: "📚",
    type: "rest_api",
    auth_type: "basic_auth",
    base_url: "https://{domain}.atlassian.net/wiki/rest/api",
    required_fields: ["domain", "email", "api_token"],
    setup_guide_url: "https://developer.atlassian.com/cloud/confluence/rest/intro/",
  },
  {
    id: "generic_rest",
    name: "Generic REST API",
    description: "Connect to any REST API with custom configuration",
    category: "generic",
    icon: "🔌",
    type: "rest_api",
    auth_type: "none",
    base_url: "",
    required_fields: ["base_url"],
    setup_guide_url: "",
  },
  {
    id: "generic_graphql",
    name: "Generic GraphQL",
    description: "Connect to any GraphQL endpoint",
    category: "generic",
    icon: "◈",
    type: "graphql",
    auth_type: "none",
    base_url: "",
    required_fields: ["base_url"],
    setup_guide_url: "",
  },
  {
    id: "generic_database",
    name: "Generic Database",
    description: "Connect to any SQL or NoSQL database",
    category: "generic",
    icon: "🗄️",
    type: "database",
    auth_type: "basic_auth",
    base_url: "",
    required_fields: ["connection_string"],
    setup_guide_url: "",
  },
];
